/**
 * GgCartToast — the single, global "added to cart" toast.
 *
 * Rendered once under the header cart icon (snippets/gg-cart-toast.liquid). Any
 * add-to-cart path dispatches a `gg:cart-toast` event on `document` carrying the
 * added item's display data; this element renders + animates it, and pulses the
 * header cart icon. Decoupled from any single buy button so PDP, quick-add, etc.
 * all resolve to the same animation.
 *
 * Event contract:
 *   document.dispatchEvent(new CustomEvent('gg:cart-toast', {
 *     detail: { title: string, brand?: string, thumb?: string }
 *   }));
 */

const EVENT_NAME = 'gg:cart-toast';

class GgCartToast extends HTMLElement {
  /** @type {HTMLImageElement | null} */
  #thumb = null;
  /** @type {HTMLElement | null} */
  #brand = null;
  /** @type {HTMLElement | null} */
  #title = null;

  connectedCallback() {
    this.#thumb = this.querySelector('.gg-cart-toast__thumb');
    this.#brand = this.querySelector('.gg-cart-toast__brand');
    this.#title = this.querySelector('.gg-cart-toast__title');

    this.addEventListener('animationend', this.#onAnimationEnd);
    document.addEventListener(EVENT_NAME, this.#onToast);
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT_NAME, this.#onToast);
    this.removeEventListener('animationend', this.#onAnimationEnd);
  }

  /** @param {Event} event */
  #onToast = (event) => {
    const { title, brand, thumb } = /** @type {CustomEvent} */ (event).detail || {};

    if (this.#title) this.#title.textContent = title || '';

    if (this.#brand) {
      if (brand) {
        this.#brand.textContent = brand;
        this.#brand.hidden = false;
      } else {
        this.#brand.textContent = '';
        this.#brand.hidden = true;
      }
    }

    if (this.#thumb) {
      if (thumb) {
        this.#thumb.src = thumb;
        this.#thumb.hidden = false;
      } else {
        this.#thumb.removeAttribute('src');
        this.#thumb.hidden = true;
      }
    }

    this.#show();
    this.#pulseCartIcon();
  };

  // The whole choreography (in → hold → out) lives in one CSS keyframe so it's
  // smooth on every template, GSAP or not. We just (re)trigger the class.
  #show() {
    this.removeAttribute('data-hidden');
    this.classList.remove('is-visible');
    void this.offsetWidth; // reflow so the animation restarts on rapid adds
    this.classList.add('is-visible');
  }

  /** @param {AnimationEvent} event */
  #onAnimationEnd = (event) => {
    if (event.target !== this) return; // ignore child animations
    // Only react to the toast's own keyframe finishing — never a stray/bubbled one.
    if (event.animationName && event.animationName.indexOf('gg-cart-toast') !== 0) return;
    this.classList.remove('is-visible');
    this.setAttribute('data-hidden', 'true');
  };

  #pulseCartIcon() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const icon = document.querySelector('.header-actions__cart-icon');
    if (!icon) return;
    icon.classList.remove('gg-cart-pulse');
    void /** @type {HTMLElement} */ (icon).offsetWidth; // restart the animation
    icon.classList.add('gg-cart-pulse');
    icon.addEventListener('animationend', () => icon.classList.remove('gg-cart-pulse'), { once: true });
  }
}

if (!customElements.get('gg-cart-toast')) {
  customElements.define('gg-cart-toast', GgCartToast);
}
